import { supabase } from "@/integrations/supabase/client";
import type { VenueSponsoredAd } from "@/hooks/useVenueSponsoredAd";

/**
 * Sponsored ads are few, but they were queried once per card (venue ad,
 * activity ad, profile ad) which meant well over a hundred requests on the
 * activities page. We now load every active ad once per viewer role and index
 * them in memory.
 */

interface AdIndex {
  byActivity: Map<string, VenueSponsoredAd>;
  byVenue: Map<string, VenueSponsoredAd>;
  byUser: Map<string, VenueSponsoredAd>;
}

const indexPromises = new Map<string, Promise<AdIndex>>();

const emptyIndex = (): AdIndex => ({
  byActivity: new Map(),
  byVenue: new Map(),
  byUser: new Map(),
});

/** All active ads, indexed by activity / venue / user. One query per role. */
export const loadSponsoredAdIndex = (isAdmin: boolean): Promise<AdIndex> => {
  const key = isAdmin ? "admin" : "public";
  let promise = indexPromises.get(key);
  if (!promise) {
    promise = (async () => {
      let query: any = (supabase as any)
        .from("sponsored_ads")
        .select(
          "id, title, title_above, youtube_video_id, thumbnail_url, venue_id, activity_id, user_id, is_public, is_pinned, created_at"
        )
        .eq("is_active", true)
        // Most relevant first, so the first row we see for a key wins.
        .order("is_pinned", { ascending: false })
        .order("created_at", { ascending: false });

      if (!isAdmin) query = query.eq("is_public", true);

      const { data, error } = await query;
      if (error) throw error;

      const index = emptyIndex();
      for (const row of (data as any[]) || []) {
        const ad = row as VenueSponsoredAd & {
          activity_id: string | null;
          user_id: string | null;
        };
        if (ad.activity_id && !index.byActivity.has(ad.activity_id)) {
          index.byActivity.set(ad.activity_id, ad);
        }
        if (ad.venue_id && !index.byVenue.has(ad.venue_id)) {
          index.byVenue.set(ad.venue_id, ad);
        }
        if (ad.user_id && !index.byUser.has(ad.user_id)) {
          index.byUser.set(ad.user_id, ad);
        }
      }
      return index;
    })().catch((error) => {
      console.error("loadSponsoredAdIndex error:", error);
      indexPromises.delete(key);
      return emptyIndex();
    });
    indexPromises.set(key, promise);
  }
  return promise;
};

/** Drop the cached ads (call after creating / editing an ad in the admin panel). */
export const invalidateSponsoredAds = () => {
  indexPromises.clear();
};
